import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatDate";
import {
  ArrowLeft,
  Download,
  FileJson,
  FileSpreadsheet,
  Trash2,
  RefreshCw,
  Clock,
  FileText,
  Zap,
  CheckCircle,
  XCircle,
  AlertCircle,
  Copy,
  ChevronDown,
  ChevronRight,
  Maximize2,
  Minimize2,
  Eye,
  EyeOff,
  Image,
  File,
} from "lucide-react";
import { v1GetWorkOrder, v1DeleteWorkOrder, v1GetWorkOrderFileUrl, v1GetWorkOrderPreviewUrl, v1GetWorkOrderFileInfo } from "../api";
import "./V1WorkOrderDetail.css";

// Model type display config
const MODEL_CONFIG = {
  xorbit: {
    name: "XOrbit",
    description: "Gemini 3.5 Flash",
    color: "#8b5cf6",
    bgColor: "#f3e8ff",
  },
  vja: {
    name: "VJA",
    description: "Qwen VL",
    color: "#0ea5e9",
    bgColor: "#e0f2fe",
  },
};

// Collapsible Section Component
const CollapsibleSection = ({ title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="v1-collapsible-section">
      <button className="v1-section-header" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        <span>{title}</span>
      </button>
      {isOpen && <div className="v1-section-content">{children}</div>}
    </div>
  );
};

// Recursive JSON Renderer
const JsonRenderer = ({ data, depth = 0 }) => {
  if (data === null || data === undefined) {
    return <span className="v1-json-null">null</span>;
  }

  if (typeof data === "boolean") {
    return <span className="v1-json-boolean">{data.toString()}</span>;
  }

  if (typeof data === "number") {
    return <span className="v1-json-number">{data}</span>;
  }

  if (typeof data === "string") {
    return <span className="v1-json-string">"{data}"</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) return <span className="v1-json-empty">[]</span>;
    return (
      <div className="v1-json-array">
        {data.map((item, index) => (
          <div key={index} className="v1-json-array-item" style={{ marginLeft: depth * 16 }}>
            <span className="v1-json-index">[{index}]</span>
            <JsonRenderer data={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof data === "object") {
    const entries = Object.entries(data);
    if (entries.length === 0) return <span className="v1-json-empty">{"{}"}</span>;
    return (
      <div className="v1-json-object">
        {entries.map(([key, value]) => (
          <div key={key} className="v1-json-property" style={{ marginLeft: depth * 16 }}>
            <span className="v1-json-key">{key}:</span>
            <JsonRenderer data={value} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  return <span>{String(data)}</span>;
};

const V1WorkOrderDetail = () => {
  const { workOrderId } = useParams();
  const navigate = useNavigate();

  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("extracted");
  const [copiedField, setCopiedField] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(true);
  const [pdfFullscreen, setPdfFullscreen] = useState(false);
  
  // File type state
  const [fileInfo, setFileInfo] = useState(null);
  const [selectedViewType, setSelectedViewType] = useState("auto"); // 'auto', 'pdf', 'image'
  const [fileLoadError, setFileLoadError] = useState(false);

  const modelConfig = MODEL_CONFIG[workOrder?.model_type] || { name: "Unknown", color: "#6b7280" };
  const fileUrl = workOrderId ? v1GetWorkOrderFileUrl(workOrderId) : null;
  const previewUrl = workOrderId ? v1GetWorkOrderPreviewUrl(workOrderId) : null;

  // Determine actual display type based on selection and file info
  const getDisplayType = useCallback(() => {
    if (selectedViewType !== "auto") {
      return selectedViewType;
    }
    // For TIFF files, we use the preview endpoint which converts to PNG
    if (fileInfo?.extension === 'tiff' || fileInfo?.extension === 'tif') {
      return 'image';
    }
    return fileInfo?.display_type || "pdf";
  }, [selectedViewType, fileInfo]);

  useEffect(() => {
    if (workOrderId) {
      fetchWorkOrder();
      fetchFileInfo();
    }
  }, [workOrderId]);

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await v1GetWorkOrder(workOrderId);
      setWorkOrder(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchFileInfo = async () => {
    try {
      setFileLoadError(false);
      const info = await v1GetWorkOrderFileInfo(workOrderId);
      setFileInfo(info);
    } catch (err) {
      console.warn("Could not fetch file info:", err.message);
      // Default to PDF if we can't get file info
      setFileInfo({ display_type: "pdf", mime_type: "application/pdf" });
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this work order? This cannot be undone.")) {
      return;
    }

    try {
      await v1DeleteWorkOrder(workOrderId);
      navigate("/v1/xorbit"); // Navigate back to list
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const handleDownloadJSON = () => {
    if (!workOrder?.extracted_data) return;

    const data = workOrder.extracted_data;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${workOrder.filename?.replace(".pdf", "") || workOrderId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadCSV = () => {
    if (!workOrder?.extracted_data) return;

    const data = workOrder.extracted_data;
    const flatData = {};

    // Flatten nested objects
    const flatten = (obj, prefix = "") => {
      for (const [key, value] of Object.entries(obj)) {
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          flatten(value, newKey);
        } else {
          flatData[newKey] = value;
        }
      }
    };
    flatten(data);

    const headers = Object.keys(flatData);
    const values = headers.map((h) => {
      const val = flatData[h];
      const str = String(val === undefined || val === null ? "" : val).replace(/"/g, '""');
      return `"${str}"`;
    });
    const csv = [headers.join(","), values.join(",")].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${workOrder.filename?.replace(".pdf", "") || workOrderId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = useCallback((text, fieldName) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    });
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle size={20} className="status-icon completed" />;
      case "failed":
        return <XCircle size={20} className="status-icon failed" />;
      case "processing":
        return <RefreshCw size={20} className="status-icon processing spin" />;
      default:
        return <AlertCircle size={20} className="status-icon pending" />;
    }
  };

  if (loading) {
    return (
      <div className="v1-detail-loading">
        <RefreshCw size={32} className="spin" />
        <p>Loading work order...</p>
      </div>
    );
  }

  if (error && !workOrder) {
    return (
      <div className="v1-detail-error">
        <XCircle size={48} />
        <h3>Error Loading Work Order</h3>
        <p>{error}</p>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="v1-detail-error">
        <AlertCircle size={48} />
        <h3>Work Order Not Found</h3>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  return (
    <div className="v1-work-order-detail">
      {/* Back Button */}
      <button className="v1-back-button" onClick={() => navigate(-1)}>
        <ArrowLeft size={18} />
        Back to Work Orders
      </button>

      {/* Header */}
      <div className="v1-detail-header" style={{ borderLeftColor: modelConfig.color }}>
        <div className="v1-detail-header-left">
          <div className="v1-detail-title">
            <h1>
              <FileText size={24} />
              {workOrder.filename || "Work Order"}
            </h1>
            <span
              className="v1-model-tag"
              style={{ backgroundColor: modelConfig.bgColor, color: modelConfig.color }}
            >
              <Zap size={14} />
              {modelConfig.name}
            </span>
          </div>
          <div className="v1-detail-meta">
            <span className="v1-meta-item">
              <strong>ID:</strong> {workOrder._id}
              <button
                className="v1-copy-btn"
                onClick={() => copyToClipboard(workOrder._id, "id")}
                title="Copy ID"
              >
                <Copy size={14} />
                {copiedField === "id" && <span className="copied-tooltip">Copied!</span>}
              </button>
            </span>
            {workOrder.trace_id && (
              <span className="v1-meta-item">
                <strong>Trace:</strong> {workOrder.trace_id}
              </span>
            )}
          </div>
        </div>

        <div className="v1-detail-header-right">
          <div className={`v1-status-badge status-${workOrder.status}`}>
            {getStatusIcon(workOrder.status)}
            {workOrder.status}
          </div>
        </div>
      </div>

      {error && (
        <div className="v1-error-banner">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      )}

      {/* Info Cards */}
      <div className="v1-info-cards">
        <div className="v1-info-card">
          <Clock size={20} />
          <div>
            <span className="v1-info-label">Created</span>
            <span className="v1-info-value">{formatDate(workOrder.created_at)}</span>
          </div>
        </div>
        <div className="v1-info-card">
          <FileText size={20} />
          <div>
            <span className="v1-info-label">Pages</span>
            <span className="v1-info-value">{workOrder.total_pages || "-"}</span>
          </div>
        </div>
        <div className="v1-info-card">
          <RefreshCw size={20} />
          <div>
            <span className="v1-info-label">Processing Time</span>
            <span className="v1-info-value">
              {workOrder.processing_time ? `${workOrder.processing_time.toFixed(2)}s` : "-"}
            </span>
          </div>
        </div>
        <div className="v1-info-card">
          <Zap size={20} />
          <div>
            <span className="v1-info-label">Model</span>
            <span className="v1-info-value">{modelConfig.description}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="v1-action-bar">
        <button 
          className={`v1-action-btn toggle ${showPdfViewer ? 'active' : ''}`}
          onClick={() => setShowPdfViewer(!showPdfViewer)}
          title={showPdfViewer ? "Hide PDF Viewer" : "Show PDF Viewer"}
        >
          {showPdfViewer ? <EyeOff size={16} /> : <Eye size={16} />}
          {showPdfViewer ? "Hide PDF" : "Show PDF"}
        </button>
        <button className="v1-action-btn primary" onClick={handleDownloadJSON}>
          <FileJson size={16} />
          JSON
        </button>
        <button className="v1-action-btn secondary" onClick={handleDownloadCSV}>
          <FileSpreadsheet size={16} />
          CSV
        </button>
        <button className="v1-action-btn refresh" onClick={fetchWorkOrder}>
          <RefreshCw size={16} />
          Refresh
        </button>
        <button className="v1-action-btn danger" onClick={handleDelete}>
          <Trash2 size={16} />
          Delete
        </button>
      </div>

      {/* Split View Container */}
      <div className={`v1-split-container ${showPdfViewer ? 'with-pdf' : 'no-pdf'} ${pdfFullscreen ? 'pdf-fullscreen' : ''}`}>
        {/* File Viewer Panel */}
        {showPdfViewer && (
          <div className="v1-pdf-panel">
            <div className="v1-pdf-header">
              <h3>
                {getDisplayType() === 'image' ? <Image size={18} /> : <FileText size={18} />}
                {fileInfo?.filename || 'Document'}
              </h3>
              <div className="v1-pdf-controls">
                {/* File Type Dropdown */}
                <select
                  className="v1-file-type-select"
                  value={selectedViewType}
                  onChange={(e) => {
                    setSelectedViewType(e.target.value);
                    setFileLoadError(false);
                  }}
                  title="Select viewer type"
                >
                  <option value="auto">
                    Auto ({fileInfo?.extension?.toUpperCase() || (fileInfo?.display_type === 'image' ? 'Image' : 'PDF')})
                  </option>
                  <option value="pdf">PDF Viewer</option>
                  <option value="image">Image/TIFF Viewer</option>
                </select>
                
                {/* File type indicator */}
                {fileInfo && (
                  <span className="v1-file-type-badge" title={fileInfo.mime_type}>
                    {fileInfo.extension?.toUpperCase() || 'FILE'}
                  </span>
                )}
                
                <button 
                  className="v1-pdf-control-btn"
                  onClick={() => setPdfFullscreen(!pdfFullscreen)}
                  title={pdfFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                >
                  {pdfFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
                <a 
                  href={fileUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="v1-pdf-control-btn"
                  title="Open in New Tab"
                >
                  <Download size={16} />
                </a>
              </div>
            </div>
            <div className="v1-pdf-viewer">
              {workOrderId ? (
                <>
                  {/* PDF Viewer */}
                  {getDisplayType() === 'pdf' && !fileLoadError && (
                    <iframe
                      src={previewUrl}
                      title="PDF Viewer"
                      className="v1-pdf-iframe"
                      onError={() => setFileLoadError(true)}
                    />
                  )}
                  
                  {/* Image Viewer with Scroller (for TIFF converted to PNG, JPEG, PNG, etc.) */}
                  {getDisplayType() === 'image' && !fileLoadError && (
                    <div className="v1-image-viewer-scroll">
                      {/* Page count indicator */}
                      {fileInfo?.total_pages > 1 && (
                        <div className="v1-page-count-badge">
                          {fileInfo.total_pages} pages
                        </div>
                      )}
                      <img
                        src={previewUrl}
                        alt={fileInfo?.filename || 'Document'}
                        className="v1-document-image-scroll"
                        onError={() => setFileLoadError(true)}
                      />
                    </div>
                  )}
                  
                  {/* Unknown type or load error */}
                  {(getDisplayType() === 'unknown' || fileLoadError) && (
                    <div className="v1-pdf-unavailable">
                      <File size={48} />
                      <p>
                        {fileLoadError 
                          ? "Unable to display this file format in browser" 
                          : "Unknown file format"}
                      </p>
                      <p className="v1-file-hint">
                        {fileInfo?.mime_type && `Type: ${fileInfo.mime_type}`}
                      </p>
                      <a 
                        href={fileUrl} 
                        download={fileInfo?.filename}
                        className="v1-download-link"
                      >
                        <Download size={16} /> Download Original File
                      </a>
                      {fileLoadError && (
                        <button 
                          className="v1-try-different-btn"
                          onClick={() => {
                            setSelectedViewType(getDisplayType() === 'pdf' ? 'image' : 'pdf');
                            setFileLoadError(false);
                          }}
                        >
                          Try {getDisplayType() === 'pdf' ? 'Image' : 'PDF'} Viewer
                        </button>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="v1-pdf-unavailable">
                  <FileText size={48} />
                  <p>File not available</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Data Panel */}
        <div className="v1-data-panel">
          {/* Tabs */}
          <div className="v1-tabs">
            <button
              className={`v1-tab ${activeTab === "extracted" ? "active" : ""}`}
              onClick={() => setActiveTab("extracted")}
            >
              Extracted Data
            </button>
            <button
              className={`v1-tab ${activeTab === "raw" ? "active" : ""}`}
              onClick={() => setActiveTab("raw")}
            >
              Raw JSON
            </button>
            <button
              className={`v1-tab ${activeTab === "signatures" ? "active" : ""}`}
              onClick={() => setActiveTab("signatures")}
            >
              Signatures
            </button>
            <button
              className={`v1-tab ${activeTab === "metadata" ? "active" : ""}`}
              onClick={() => setActiveTab("metadata")}
            >
              Metadata
            </button>
          </div>

          {/* Tab Content */}
          <div className="v1-tab-content">
            {activeTab === "extracted" && (
              <div className="v1-extracted-data">
                {workOrder.extracted_data ? (
                  Object.entries(workOrder.extracted_data).map(([sectionKey, sectionData]) => (
                    <CollapsibleSection key={sectionKey} title={sectionKey}>
                      {typeof sectionData === "object" && sectionData !== null ? (
                        <div className="v1-fields-table">
                          <table>
                            <thead>
                              <tr>
                                <th>Field</th>
                                <th>Value</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(sectionData).map(([fieldKey, fieldValue]) => (
                                <tr key={fieldKey}>
                                  <td className="field-name">{fieldKey}</td>
                                  <td className="field-value">
                                    {typeof fieldValue === "object"
                                      ? JSON.stringify(fieldValue)
                                      : String(fieldValue ?? "")}
                                  </td>
                                  <td className="field-actions">
                                    <button
                                      className="v1-copy-btn"
                                      onClick={() =>
                                        copyToClipboard(
                                          typeof fieldValue === "object"
                                            ? JSON.stringify(fieldValue)
                                            : String(fieldValue ?? ""),
                                          `${sectionKey}.${fieldKey}`
                                        )
                                      }
                                    >
                                      <Copy size={14} />
                                      {copiedField === `${sectionKey}.${fieldKey}` && (
                                        <span className="copied-tooltip">Copied!</span>
                                      )}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="v1-simple-value">{String(sectionData)}</div>
                      )}
                    </CollapsibleSection>
                  ))
                ) : (
                  <div className="v1-no-data">
                    <AlertCircle size={48} />
                    <p>No extracted data available</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "raw" && (
              <div className="v1-raw-json">
                <div className="v1-raw-json-header">
                  <button onClick={handleDownloadJSON}>
                    <Download size={16} /> Download
                  </button>
                  <button
                    onClick={() =>
                      copyToClipboard(JSON.stringify(workOrder.extracted_data, null, 2), "raw")
                    }
                  >
                    <Copy size={16} /> Copy
                    {copiedField === "raw" && <span className="copied-tooltip">Copied!</span>}
                  </button>
                </div>
                <pre className="v1-json-viewer">
                  {workOrder.extracted_data ? (
                    <JsonRenderer data={workOrder.extracted_data} />
                  ) : (
                    "No data available"
                  )}
                </pre>
              </div>
            )}

            {activeTab === "signatures" && (
              <div className="v1-signatures-section">
                <h3>Signature Detection</h3>
                {(() => {
                  const signatureImages = workOrder.extracted_data?.signature_images || {};
                  const signatureKeys = Object.keys(signatureImages).filter(key => {
                    const value = signatureImages[key];
                    return value && value.trim() !== '';
                  });
                  
                  if (signatureKeys.length === 0) {
                    return (
                      <div className="v1-no-signatures">
                        <AlertCircle size={48} />
                        <p>No signatures detected in this document</p>
                        <span className="v1-signatures-hint">
                          Signatures are automatically extracted using AWS Textract when processing new documents.
                        </span>
                      </div>
                    );
                  }
                  
                  // Format signature label for display
                  const formatLabel = (key) => {
                    return key
                      .replace(/_/g, ' ')
                      .replace(/P(\d+)$/, '(Page $1)')
                      .replace(/(\d+)$/, ' $1');
                  };
                  
                  return (
                    <div className="v1-signatures-grid">
                      {signatureKeys.map(key => {
                        const base64Image = signatureImages[key];
                        return (
                          <div key={key} className="v1-signature-card">
                            <div className="v1-signature-header">
                              <FileText size={18} />
                              <span>{formatLabel(key)}</span>
                            </div>
                            <div className="v1-signature-image-container">
                              <img src={base64Image} alt={formatLabel(key)} className="v1-signature-image" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {activeTab === "metadata" && (
              <div className="v1-metadata">
                <table className="v1-metadata-table">
                  <tbody>
                    <tr>
                      <td>Work Order ID</td>
                      <td>{workOrder._id}</td>
                    </tr>
                    <tr>
                      <td>Filename</td>
                      <td>{workOrder.filename || "-"}</td>
                    </tr>
                    <tr>
                      <td>Model Type</td>
                      <td>{workOrder.model_type}</td>
                    </tr>
                    <tr>
                      <td>Status</td>
                      <td>{workOrder.status}</td>
                    </tr>
                    <tr>
                      <td>Total Pages</td>
                      <td>{workOrder.total_pages || "-"}</td>
                    </tr>
                    <tr>
                      <td>Processing Time</td>
                      <td>{workOrder.processing_time ? `${workOrder.processing_time.toFixed(3)}s` : "-"}</td>
                    </tr>
                    <tr>
                      <td>Created At</td>
                      <td>{formatDate(workOrder.created_at)}</td>
                    </tr>
                    <tr>
                      <td>Updated At</td>
                      <td>{formatDate(workOrder.updated_at)}</td>
                    </tr>
                    {workOrder.trace_id && (
                      <tr>
                        <td>Trace ID</td>
                        <td>{workOrder.trace_id}</td>
                      </tr>
                    )}
                    {workOrder.api_log && (
                      <>
                        <tr className="v1-metadata-section">
                          <td colSpan={2}>API Request Details</td>
                        </tr>
                        <tr>
                          <td>Method</td>
                          <td>{workOrder.api_log.method || "-"}</td>
                        </tr>
                        <tr>
                          <td>Endpoint</td>
                          <td>{workOrder.api_log.endpoint || "-"}</td>
                        </tr>
                        <tr>
                          <td>IP</td>
                          <td>{workOrder.api_log.ip_address || "-"}</td>
                        </tr>
                        <tr>
                          <td>Status</td>
                          <td>
                            {workOrder.api_log.status || "-"}
                            {workOrder.api_log.status_code ? ` (${workOrder.api_log.status_code})` : ""}
                          </td>
                        </tr>
                        <tr>
                          <td>Request payload</td>
                          <td>
                            <pre className="v1-metadata-payload">
                              {workOrder.api_log.request_params &&
                              Object.keys(workOrder.api_log.request_params).length > 0
                                ? JSON.stringify(workOrder.api_log.request_params, null, 2)
                                : "-"}
                            </pre>
                          </td>
                        </tr>
                        <tr>
                          <td>Response Time</td>
                          <td>
                            {workOrder.api_log.processing_time_seconds != null
                              ? `${parseFloat(workOrder.api_log.processing_time_seconds).toFixed(3)}s`
                              : "-"}
                          </td>
                        </tr>
                        <tr>
                          <td>Created By</td>
                          <td>{workOrder.api_log.created_by || "-"}</td>
                        </tr>
                        <tr>
                          <td>Timestamp</td>
                          <td>{formatDate(workOrder.api_log.timestamp)}</td>
                        </tr>
                      </>
                    )}
                    {workOrder.error && (
                      <tr className="error-row">
                        <td>Error</td>
                        <td>{workOrder.error}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default V1WorkOrderDetail;
